import React, { useEffect } from "react";
import { BadgeCheck, Factory, MapPin, ShieldCheck, Truck } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

import { SafeImage } from "@/components/SafeImage";

import { COMPANY, IMAGES, SEO } from "@/mock";
import { SectionTitle, StatPill } from "@/pages/_bits";
import { Container, setTitle } from "@/pages/_shared";

export default function AboutPage() {
  useEffect(() => setTitle("About Us"), []);

  return (
    <div>
      <section className="py-14">
        <Container>
          <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex flex-wrap gap-2">
                <Badge className="bg-emerald-100 text-emerald-950 border-emerald-200">{COMPANY.certification}</Badge>
                <Badge className="bg-amber-100 text-amber-950 border-amber-200">{COMPANY.years}+ years</Badge>
              </div>

              <h1 className="mt-5 text-3xl sm:text-5xl font-extrabold tracking-tight">
                National Wire Products – Leading Steel Wire Products Manufacturer and Supplier in Northeast India
              </h1>

              <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl">
                With over 25 years of experience in the sector, National Wire Products is a manufacturing and
                supply company based in {COMPANY.locationShort}. We have built a strong reputation as a
                trusted GI wire, fencing and gabion box supplier, serving infrastructure contractors,
                EPC companies, government departments, and industrial buyers acrross the Northeast
                India region.</p>
               <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl">
                We specialize in a comprehensive range of steel wire and fencing products, including
                GI Wire, MS Wire, Pre-Stressed Concrete Wire (HT Wire), Chain Link Fencing, Barbed Wire,
                Concertina Wire, Razor Blade Wire (RBT), Gabion Boxes, GI Wire Netting Sheets, Boulder
                Stone Nets, and fencing nets.
                All products are manufactured using premium-grade raw materials and are designed to deliver
                long-term durability, and reliable performance in demanding construction and infrastructure
                environments.</p>
                <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl">
                As a segment leader in Assam and Northeast India, we follow strict quality control and
                Indian Standard compliance, ensuring consistent product quality for road and highway
                projects, riverbank protection, railway infrastructure, border fencing, and industrial
                applications.</p>
                <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl">
                Our commitment to reliable supply, competitive pricing, and timely delivery
                makes National Wire Products a preferred partner for bulk orders and large-scale projects
                across the northeastern states - Assam, Arunachal Pradesh, Meghalaya, Nagaland, Manipur, Mizoram, and Tripura.
                Driven by a focus on quality, durability, and security, National Wire Products continues
                to support region-building infrastructure with trusted wire and fencing solutions that stand
                the test of time.
              </p>

              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                <StatPill icon={Factory} title="Manufacturing Capability" desc="Reliable production for bulk orders" />
                <StatPill icon={ShieldCheck} title="Quality Commitment" desc="Inspection, testing and documentation" />
                <StatPill icon={MapPin} title="Service Area" desc="Assam + Northeast India coverage" />
                <StatPill icon={Truck} title="Delivery Planning" desc="Dispatch aligned to project timelines" />
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-3xl border border-border/60">
                <SafeImage src={IMAGES.facility} alt="Factory / facility" imgClassName="h-[420px] w-full object-cover" fallbackLabel="Factory" />
              </div>
            </div>
          </div>

          <Separator className="my-12" />

          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <SectionTitle
                eyebrow="Mission"
                title="Make procurement easier for infrastructure teams"
                desc="We focus on consistent manufacturing, dependable supply and clear communication—so your site team gets what it needs without delays."
              />
            </div>
            <div className="lg:col-span-6">
              <SectionTitle
                eyebrow="Vision"
                title="Become the preferred wire & fencing supplier in Northeast India"
                desc="By improving quality discipline, expanding product range and maintaining the trust of repeat buyers and contractors."
              />
            </div>
          </div>

          <div className="mt-10 rounded-3xl border border-border/60 bg-secondary/25 p-6">
            <div className="flex items-center gap-2 text-sm font-extrabold tracking-tight">
              <BadgeCheck className="h-4 w-4 text-emerald-700" /> Service Area (Northeast India)
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {COMPANY.serviceArea.map((s) => (
                <Badge key={s} variant="secondary" className="bg-background/70">
                  {s}
                </Badge>
              ))}
            </div>

            <div className="mt-5 text-sm font-extrabold tracking-tight">Major cities served</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {SEO.majorCities.map((c) => (
                <Badge key={c} className="bg-amber-100 text-amber-950 border-amber-200">
                  {c}
                </Badge>
              ))}
            </div>

            <p className="mt-4 text-sm text-muted-foreground">
              For bulk orders and project supply, share your location and BOQ—our team will confirm feasible lead time and transport.
            </p>
          </div>
        </Container>
      </section>
    </div>
  );
}
